import { useTranslation } from "react-i18next";

type Product = {
  name: string;
  category: string;
  description: string;
  tech: string;
  highlight: string;
};

const InnovationsSection = () => {
  const { t } = useTranslation();

  const blockchainProducts: Product[] = [
    {
      name: t("BOGOWI Tourism Platform"),
      category: t("Blockchain Infrastructure"),
      description: t(
        "Complete on-chain ecosystem for sustainable tourism with booking contracts, NFT tickets (ERC-721), native token (BOGO/ERC-20), and DAO governance with automated revenue routing."
      ),
      tech: "Solidity, Hardhat, OpenZeppelin, Multisig",
      highlight: t("5% auto-allocation to DAO treasury"),
    },
    {
      name: t("Smart Contract Suite"),
      category: t("Modular Web3 Architecture"),
      description: t(
        "Role-based access control with timelocked execution, pause/burn/mint hooks for operational safety, and separation between governance and business logic."
      ),
      tech: "Access Control, Security Patterns, Governance",
      highlight: t("Zero-trust security model"),
    },
    {
      name: t("Real-Time CDC Pipeline"),
      category: t("Distributed State Management"),
      description: t(
        "Change Data Capture streaming for RAFT consensus with exactly-once semantics, preserving transaction order for distributed logs at 500+ events/s."
      ),
      tech: "MySQL, Debezium, Kafka, ClickHouse",
      highlight: t("<5s end-to-end latency, 99.9% uptime"),
    },
  ];

  const aiInfrastructure: Product[] = [
    {
      name: t("AURA Distributed LLM Platform"),
      category: t("AI Infrastructure"),
      description: t(
        "Production-grade distributed inference system with RAFT consensus, multi-model orchestration (TinyLlama to CodeLlama 13B), and hot-swappable loading with 75% memory reduction."
      ),
      tech: "FastAPI, llama.cpp, PostgreSQL+pgvector, CUDA",
      highlight: t("Zero-downtime model switching"),
    },
  ];

  const analyticsProducts: Product[] = [
    {
      name: t("NL→SQL Intelligence Engine"),
      category: t("Enterprise Analytics"),
      description: t(
        "Schema-aware natural language compiler that generates safe, optimized SQL with auto join-path discovery and policy guardrails. Processes 1M+ invoice lines in <2s."
      ),
      tech: "GPT-4, ClickHouse, FastAPI, SQL Optimization",
      highlight: t("€50K+ projected savings per location"),
    },
    {
      name: t("Multi-Tenant Analytics Platform"),
      category: t("Real-Time Intelligence"),
      description: t(
        "Hierarchical organization system with supplier optimization recommendations, dual visualization pipeline (charts + ASCII), and 15-25% category savings discovery."
      ),
      tech: "Python, ClickHouse, Matplotlib, Asyncio",
      highlight: t("50+ concurrent locations supported"),
    },
  ];

  const mobileProducts: Product[] = [
    {
      name: t("Cross-Platform Mobile Solutions"),
      category: t("React Native & Kotlin Multiplatform"),
      description: t(
        "Native and cross-platform mobile applications using industry-leading frameworks. Each project requires a comprehensive technology assessment to determine the optimal approach."
      ),
      tech: "React Native, Kotlin Multiplatform, Swift, Java/Kotlin",
      highlight: t("Technology-agnostic approach"),
    },
    {
      name: t("Mobile Architecture Consulting"),
      category: t("Technology Assessment"),
      description: t(
        "Strategic analysis to determine the best available technology stack based on project requirements, team expertise, performance needs, and long-term maintenance considerations."
      ),
      tech: "Architecture Design, Technology Evaluation, Performance Analysis",
      highlight: t("Custom technology roadmaps"),
    },
  ];

  const Row = ({ p }: { p: Product }) => (
    <li className="grid grid-cols-12 gap-x-6 gap-y-3 border-t border-ink-14 pt-5 pb-5">
      <div className="col-span-12 md:col-span-4">
        <h4 className="text-h4 font-medium tracking-tight text-ink">{p.name}</h4>
        <p className="swiss-caption mt-2">{p.category}</p>
      </div>
      <p className="col-span-12 md:col-span-4 text-body text-ink-70 max-w-measure">
        {p.description}
      </p>
      <div className="col-span-6 md:col-span-2">
        <span className="swiss-caption block mb-2">{t("Tech")}</span>
        <p className="text-body text-ink">{p.tech}</p>
      </div>
      <div className="col-span-6 md:col-span-2">
        <span className="swiss-caption block mb-2">{t("Metric")}</span>
        <p className="text-body text-ink">{p.highlight}</p>
      </div>
    </li>
  );

  const Group = ({
    num,
    title,
    items,
  }: {
    num: string;
    title: string;
    items: Product[];
  }) => (
    <div className="col-span-12">
      <div className="flex items-baseline gap-4 mb-5">
        <span className="swiss-num">{num}</span>
        <h3 className="text-h3 font-medium tracking-tight text-ink">{title}</h3>
      </div>
      <ul className="border-b border-ink-14">
        {items.map((p, i) => (
          <Row key={i} p={p} />
        ))}
      </ul>
    </div>
  );

  return (
    <section className="swiss-section">
      <div className="swiss-grid">
        <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
          <span className="swiss-num">04 / {t("Capabilities")}</span>
        </div>

        <div className="col-span-12 lg:col-span-10">
          <h2 className="text-h2 font-bold tracking-tight text-ink mb-9 max-w-[22ch]">
            {t(
              "From loyalty tokens to asset marketplaces, white-label modules that let your business own the future."
            )}
          </h2>

          <div className="grid grid-cols-12 gap-y-9">
            <Group
              num="I"
              title={t("Blockchain Infrastructure")}
              items={blockchainProducts}
            />
            <Group num="II" title={t("AI Infrastructure")} items={aiInfrastructure} />
            <Group
              num="III"
              title={t("Data Analytics Platforms")}
              items={analyticsProducts}
            />
            <Group
              num="IV"
              title={t("Mobile Applications")}
              items={mobileProducts}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default InnovationsSection;
